import { Box, useTheme } from "@mui/material"
import React, { useEffect, useRef } from "react"
import { suppressNextClick } from "../../../helpers/dragClick.helpers"
import {
    anchorResizedBox,
    resizeCursor,
    toCssMatrix,
    cornerNormalAngle,
    handleTravelAngle,
    toLocalDelta,
    uprightCounterTransform,
} from "../../../helpers/orientedResize.helpers"
import type { Orientation } from "../../../helpers/selectionChrome.helpers"
import type { SnappedAxes } from "../../../helpers/snap.helpers"
import { RESIZE_HANDLE_ATTRIBUTE, setGlobalCursor } from "../../../hooks/editor/useHoldDrag"
import { useAppSelector } from "../../../hooks/useRedux"

interface PositionType {
    position: "relative" | "absolute"
    left: number
    top: number
    width: number
    height: number
}

export type Axis = "x" | "y" | "both" | "none"

interface Props {
    /**
     * The object's own parent-relative geometry. Resizing writes parent-relative top/left,
     * so every number that ends up in the store is derived from THIS.
     */
    position: PositionType
    /**
     * Where the handle is DRAWN. The selection chrome lives in the canvas coordinate space
     * (see SelectionOverlay), which is not the object's — the two must not be conflated.
     */
    renderPosition: PositionType
    /**
     * Where the resized box ends up, in the same space as `position`.
     *
     * The handle only works out the new geometry; committing it is the caller's business.
     * That is what lets the very same handle drive a single object (write its own styles)
     * and a whole multiselection (scale every member inside the new bounding box).
     */
    onResize: (box: PositionBox) => void
    /** A resize gesture started on this handle. */
    onResizeStart: () => void
    /** The box under the pointer, on every move — for the live preview. Same space as `onResize`. */
    onResizing: (box: PositionBox) => void
    /** The gesture ended without a resize (no movement, Escape, pointercancel). */
    onResizeCancel: () => void
    /**
     * Snap to objects: pull the pointer delta (canvas units) so the dragged edges land on
     * another object. Runs before the box is worked out, so anchoring, mirroring and
     * shift-proportions all apply to the snapped edge.
     *
     * @param bypass ctrl is held — snapping is suspended, as in Figma
     */
    snapDelta: (
        direction: Direction,
        delta: ResizeDelta,
        bypass: boolean
    ) => { delta: ResizeDelta; snapped: SnappedAxes }
    canvasScaleCoef: number
    direction: Direction
    axis?: Axis
    disabled: boolean
    /**
     * A turned object (see measureOrientedChrome): the handle sits on its turned side, so the
     * pointer is read along the object's own axes and the result is put back where the
     * opposite side stays on the canvas. `snapDelta` then gets — and returns — the delta
     * along the object's own sides.
     */
    orientation?: Orientation
    /** the object's transform-origin style, which a turn happens about */
    transformOrigin?: string
}

export type PositionBox = {
    top: number
    left: number
    width: number
    height: number
    isMirroredX: boolean
    isMirroredY: boolean
}

export type ResizeDelta = { x: number; y: number }

export enum Direction {
    TopLeft = "topLeft",
    Top = "top",
    TopRight = "topRight",
    Right = "right",
    Bottom = "bottom",
    BottomRight = "bottomRight",
    BottomLeft = "bottomLeft",
    Left = "left",
}

/** Corners last: they are drawn on top of the edge zones they overlap. */
export const DRAGGABLE_ITEMS: { direction: Direction; axis: Axis }[] = [
    { direction: Direction.Right, axis: "x" },
    { direction: Direction.Left, axis: "x" },
    { direction: Direction.Top, axis: "y" },
    { direction: Direction.Bottom, axis: "y" },
    { direction: Direction.BottomRight, axis: "both" },
    { direction: Direction.BottomLeft, axis: "both" },
    { direction: Direction.TopLeft, axis: "both" },
    { direction: Direction.TopRight, axis: "both" },
]

export const LEFT_DIRECTIONS = [Direction.TopLeft, Direction.Left, Direction.BottomLeft]
export const RIGHT_DIRECTIONS = [Direction.TopRight, Direction.Right, Direction.BottomRight]
export const TOP_DIRECTIONS = [Direction.TopLeft, Direction.Top, Direction.TopRight]
export const BOTTOM_DIRECTIONS = [Direction.BottomLeft, Direction.Bottom, Direction.BottomRight]

const CORNER_DIRECTIONS: Direction[] = [
    Direction.TopLeft,
    Direction.TopRight,
    Direction.BottomLeft,
    Direction.BottomRight,
]

/**
 * The direction each handle resizes along, in degrees (0 = to the right, clockwise like the
 * screen) — what its double-arrow cursor points along before the object is turned.
 */
const HANDLE_ANGLES: Record<Direction, number> = {
    [Direction.Left]: 0,
    [Direction.Right]: 0,
    [Direction.Top]: 90,
    [Direction.Bottom]: 90,
    [Direction.TopLeft]: 45,
    [Direction.BottomRight]: 45,
    [Direction.TopRight]: -45,
    [Direction.BottomLeft]: -45,
}

/** Which way each handle travels in the box's own space: outwards along x and / or y. */
const HANDLE_TRAVEL: Record<Direction, { x: number; y: number }> = {
    [Direction.Left]: { x: -1, y: 0 },
    [Direction.Right]: { x: 1, y: 0 },
    [Direction.Top]: { x: 0, y: -1 },
    [Direction.Bottom]: { x: 0, y: 1 },
    [Direction.TopLeft]: { x: -1, y: -1 },
    [Direction.TopRight]: { x: 1, y: -1 },
    [Direction.BottomLeft]: { x: -1, y: 1 },
    [Direction.BottomRight]: { x: 1, y: 1 },
}

/** Screen px. Divided by the canvas scale so the chrome keeps its size at any zoom. */
const EDGE_HIT_SIZE_PX = 6
export const CORNER_SIZE_PX = 8

/**
 * One scale for both sides of a corner resize, from the scale each side would get on its own.
 *
 * Growing (both positive) takes the larger one and mirrored (both negative) the more negative
 * one, so the box always reaches the pointer. In between, where one side has crossed its
 * anchor and the other has not, it is their sum: equal to the one that has not crossed at
 * either boundary and zero on the anti-diagonal, so the box shrinks to nothing and comes out
 * mirrored instead of jumping. Picking by "which side changed more" flipped between growing
 * and shrinking whenever the two sides went opposite ways, and the box jumped with it.
 */
const cornerScale = (scaleX: number, scaleY: number): number => {
    if (scaleX >= 0 && scaleY >= 0) return Math.max(scaleX, scaleY)
    if (scaleX <= 0 && scaleY <= 0) return Math.min(scaleX, scaleY)
    return scaleX + scaleY
}

const DraggableItemBase = ({
    canvasScaleCoef,
    direction,
    axis,
    position,
    renderPosition,
    onResize,
    onResizeStart,
    onResizing,
    onResizeCancel,
    snapDelta,
    disabled,
    orientation,
    transformOrigin,
}: Props) => {
    const theme = useTheme()
    // primitives instead of the whole `editor.value`: eight of these render per selected
    // object and they must not re-render when an unrelated editor setting changes
    const canvasZoom = useAppSelector((state) => state.editor.value.settings.canvasZoom)
    const canvasSnapToGrid = useAppSelector(
        (state) => state.editor.value.settings.canvasSnapToGridOn
    )
    const draggableScale = (canvasScaleCoef * canvasZoom) / 100

    const isCorner = CORNER_DIRECTIONS.indexOf(direction) !== -1
    // compensated for the CSS transform scale, so the handles stay a constant screen size
    const hitSize = (isCorner ? CORNER_SIZE_PX : EDGE_HIT_SIZE_PX) / draggableScale
    // every zone straddles its edge instead of sitting inside the object: half in, half out,
    // which is what makes the corner squares read as centred on the corner
    const offset = hitSize / 2
    const canvasSnapGrid = canvasSnapToGrid ? 20 : 1

    const resultStyles = () => {
        const top = Number(renderPosition.top)
        const left = Number(renderPosition.left)
        const width = Number(renderPosition.width)
        const height = Number(renderPosition.height)

        switch (direction) {
            case Direction.Top:
                return {
                    top: top - offset + "px",
                    left: left + "px",
                    width: width + "px",
                    height: hitSize + "px",
                }
            case Direction.TopRight:
                return {
                    top: top - offset + "px",
                    left: left + width - offset + "px",
                    width: hitSize + "px",
                    height: hitSize + "px",
                }
            case Direction.Right:
                return {
                    top: top + "px",
                    left: left + width - offset + "px",
                    width: hitSize + "px",
                    height: height + "px",
                }
            case Direction.BottomRight:
                return {
                    top: top + height - offset + "px",
                    left: left + width - offset + "px",
                    width: hitSize + "px",
                    height: hitSize + "px",
                }
            case Direction.Bottom:
                return {
                    top: top + height - offset + "px",
                    left: left + "px",
                    width: width + "px",
                    height: hitSize + "px",
                }
            case Direction.BottomLeft:
                return {
                    top: top + height - offset + "px",
                    left: left - offset + "px",
                    width: hitSize + "px",
                    height: hitSize + "px",
                }
            case Direction.Left:
                return {
                    top: top + "px",
                    left: left - offset + "px",
                    width: hitSize + "px",
                    height: height + "px",
                }
            case Direction.TopLeft:
                return {
                    top: top - offset + "px",
                    left: left - offset + "px",
                    width: hitSize + "px",
                    height: hitSize + "px",
                }
        }
    }
    const resultStylesValues = resultStyles()
    /**
     * The same arrow for every handle (see resizeCursor), turned along the object's sides
     * when it is turned — on hover and, through the document lock below, for the whole
     * gesture.
     */
    const cursor = disabled
        ? "default"
        : resizeCursor(
              orientation
                  ? isCorner
                      ? // a corner as before: between its two sides' normals
                        cornerNormalAngle(orientation.total, [
                            { x: 0, y: HANDLE_TRAVEL[direction].y },
                            { x: HANDLE_TRAVEL[direction].x, y: 0 },
                        ])
                      : // a side the way it travels: parallel to the sides next to it
                        handleTravelAngle(orientation.total, HANDLE_TRAVEL[direction])
                  : HANDLE_ANGLES[direction]
          )

    /**
     * @param snapped the axis was pulled onto another object — keep the value as it is (bar
     *        float noise), rounding it onto the grid would land the edge off the guide
     */
    const calculateValue = (dataAxis: number, objectPositionValue: number, snapped: boolean) => {
        const value = Number(objectPositionValue) + Number(dataAxis)
        if (snapped) return Math.round(value * 100) / 100
        return Math.round(value / canvasSnapGrid) * canvasSnapGrid
    }

    /**
     * Shift keeps the proportions of the box the handle was grabbed at, the way Figma does it.
     *
     * A corner follows the pointer on whichever axis it is FURTHER along, so the dragged edge
     * on that axis sits exactly under the pointer and the box always reaches it. The opposite
     * corner stays put. An edge scales the other side along with it, centred, so the box grows
     * symmetrically across the axis that is not dragged.
     *
     * Works on the SIGNED sizes from the free resize, so dragging past the opposite corner
     * still mirrors the box — the mirror normalisation below runs on the result.
     */
    const keepAspectRatio = (direction: Direction, freeWidth: number, freeHeight: number) => {
        const { width, height, left, top } = position
        const scaleX = freeWidth / width
        const scaleY = freeHeight / height

        let newWidth: number
        let newHeight: number
        if (direction === Direction.Left || direction === Direction.Right) {
            newWidth = freeWidth
            newHeight = Math.abs(scaleX) * height
        } else if (direction === Direction.Top || direction === Direction.Bottom) {
            newWidth = Math.abs(scaleY) * width
            newHeight = freeHeight
        } else {
            const scale = cornerScale(scaleX, scaleY)
            newWidth = scale * width
            newHeight = scale * height
        }
        newWidth = Math.round(newWidth)
        newHeight = Math.round(newHeight)

        const movesLeft = LEFT_DIRECTIONS.indexOf(direction) !== -1
        const movesRight = RIGHT_DIRECTIONS.indexOf(direction) !== -1
        const movesTop = TOP_DIRECTIONS.indexOf(direction) !== -1
        const movesBottom = BOTTOM_DIRECTIONS.indexOf(direction) !== -1

        // the edge opposite to the dragged one is the anchor; an axis nobody drags is centred
        let newLeft = left
        if (movesLeft) newLeft = left + width - newWidth
        else if (!movesRight) newLeft = Math.round(left + (width - Math.abs(newWidth)) / 2)

        let newTop = top
        if (movesTop) newTop = top + height - newHeight
        else if (!movesBottom) newTop = Math.round(top + (height - Math.abs(newHeight)) / 2)

        return { newWidth, newHeight, newLeft, newTop }
    }

    const resultResize = (
        direction: Direction,
        data: ResizeDelta,
        shiftKey: boolean,
        snapped: SnappedAxes
    ) => {
        let newWidth = position.width
        let newHeight = position.height
        let newLeft = position.left
        let newTop = position.top

        switch (direction) {
            case Direction.TopLeft:
                newHeight = calculateValue(-data.y, position.height, snapped.y)
                newTop = calculateValue(data.y, position.top, snapped.y)
                newWidth = calculateValue(-data.x, position.width, snapped.x)
                newLeft = calculateValue(data.x, position.left, snapped.x)
                break

            case Direction.Top:
                newHeight = calculateValue(-data.y, position.height, snapped.y)
                newTop = calculateValue(data.y, position.top, snapped.y)
                break

            case Direction.TopRight:
                newHeight = calculateValue(-data.y, position.height, snapped.y)
                newTop = calculateValue(data.y, position.top, snapped.y)
                newWidth = calculateValue(data.x, position.width, snapped.x)
                break

            case Direction.Right:
                newWidth = calculateValue(data.x, position.width, snapped.x)
                break

            case Direction.BottomRight:
                newHeight = calculateValue(data.y, position.height, snapped.y)
                newWidth = calculateValue(data.x, position.width, snapped.x)
                break

            case Direction.Bottom:
                newHeight = calculateValue(data.y, position.height, snapped.y)
                break

            case Direction.BottomLeft:
                newHeight = calculateValue(data.y, position.height, snapped.y)
                newWidth = calculateValue(-data.x, position.width, snapped.x)
                newLeft = calculateValue(data.x, position.left, snapped.x)
                break

            case Direction.Left:
                newWidth = calculateValue(-data.x, position.width, snapped.x)
                newLeft = calculateValue(data.x, position.left, snapped.x)
                break

            default:
                break
        }

        if (shiftKey && position.width > 0 && position.height > 0) {
            const proportional = keepAspectRatio(direction, newWidth, newHeight)
            newWidth = proportional.newWidth
            newHeight = proportional.newHeight
            newLeft = proportional.newLeft
            newTop = proportional.newTop
        }

        /**
         * Dragging a handle past the opposite edge mirrors the box instead of collapsing it.
         *
         * The maths above produces a negative width once the dragged edge crosses the one
         * that stays put, and a negative width is exactly what "the box now extends the
         * other way" means. CSS cannot express it, so the box is normalised: the anchor
         * edge — which `newLeft + newWidth` already IS in that case — becomes the new origin
         * and the size becomes its own magnitude.
         *
         * It used to be clamped with `Math.max(0, …)`, which pinned the size at zero but let
         * `left` / `top` keep travelling, so the object collapsed and then walked away from
         * the pointer.
         */
        const isMirroredX = newWidth < 0
        const isMirroredY = newHeight < 0

        return {
            width: Math.abs(newWidth),
            height: Math.abs(newHeight),
            left: isMirroredX ? newLeft + newWidth : newLeft,
            top: isMirroredY ? newTop + newHeight : newTop,
            isMirroredX,
            isMirroredY,
        }
    }

    /**
     * The gesture, tracked by hand rather than through react-draggable.
     *
     * react-draggable moved the grabbed handle with a transform of its own, while the other
     * seven stayed where they were and a separate outline previewed the new box. Now the
     * caller previews the resize on the object itself and redraws the whole chrome — all
     * eight handles included — from the live box, the way Figma does it. A handle that also
     * carried its own transform would then move twice as far as the pointer.
     */
    const gestureRef = useRef<{
        pointerId: number
        startX: number
        startY: number
        lastX: number
        lastY: number
        lastBox: PositionBox | null
    } | null>(null)

    const boxFromPointer = (
        clientX: number,
        clientY: number,
        shiftKey: boolean,
        ctrlKey: boolean
    ) => {
        const gesture = gestureRef.current!
        const canvasDelta: ResizeDelta = {
            x: (clientX - gesture.startX) / draggableScale,
            y: (clientY - gesture.startY) / draggableScale,
        }
        // along the object's own sides when it is turned; an edge handle only moves its axis
        const ownDelta = orientation ? toLocalDelta(orientation.total, canvasDelta) : canvasDelta
        const pointerDelta: ResizeDelta = {
            x: axis === "y" ? 0 : ownDelta.x,
            y: axis === "x" ? 0 : ownDelta.y,
        }
        const { delta, snapped } = snapDelta(direction, pointerDelta, ctrlKey)
        const resized = resultResize(direction, delta, shiftKey, snapped)
        const original = {
            left: Number(position.left),
            top: Number(position.top),
            width: Number(position.width),
            height: Number(position.height),
        }
        // the styles can hold words (a text's `auto` width): nothing to anchor from then
        const canAnchor = Object.values(original).every(Number.isFinite)
        const newPosition =
            orientation && canAnchor
                ? {
                      ...resized,
                      ...anchorResizedBox(original, resized, orientation.own, transformOrigin),
                  }
                : resized
        return {
            width: Number(newPosition.width),
            height: Number(newPosition.height),
            left: Number(newPosition.left),
            top: Number(newPosition.top),
            isMirroredX: newPosition.isMirroredX,
            isMirroredY: newPosition.isMirroredY,
        }
    }

    /** `react-draggable` used to do this (its `enableUserSelectHack`) — see useHoldDrag. */
    const lockDocument = (cursor: string) => {
        document.body.style.userSelect = "none"
        setGlobalCursor(cursor)
    }
    const unlockDocument = () => {
        document.body.style.userSelect = ""
        setGlobalCursor(null)
    }

    /** Recomputes the box for the pointer at (clientX, clientY) and previews it. */
    const previewAt = (clientX: number, clientY: number, shiftKey: boolean, ctrlKey: boolean) => {
        const gesture = gestureRef.current
        if (!gesture) return
        gesture.lastX = clientX
        gesture.lastY = clientY
        if (!gesture.lastBox && clientX === gesture.startX && clientY === gesture.startY) return

        const box = boxFromPointer(clientX, clientY, shiftKey, ctrlKey)
        gesture.lastBox = box
        onResizing(box)
    }

    /** Ends the gesture; commits the last previewed box unless `commit` is false. */
    const finishGesture = (commit: boolean) => {
        const gesture = gestureRef.current
        if (!gesture) return
        gestureRef.current = null
        unlockDocument()

        if (commit && gesture.lastBox) {
            // released off the handle, the click would land on whatever is under the pointer
            // (or their common ancestor, the canvas) and change the selection
            suppressNextClick()
            onResize(gesture.lastBox)
        } else {
            onResizeCancel()
        }
    }

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (disabled || event.button !== 0) return
        // the canvas below must neither start a move nor see a click that deselects
        event.stopPropagation()
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)

        gestureRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            lastX: event.clientX,
            lastY: event.clientY,
            lastBox: null,
        }
        lockDocument(String(cursor ?? "default"))
        onResizeStart()
    }

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const gesture = gestureRef.current
        if (!gesture || event.pointerId !== gesture.pointerId) return
        previewAt(event.clientX, event.clientY, event.shiftKey, event.ctrlKey)
    }

    /**
     * The box is worked out again from the RELEASE point, not taken from the last move.
     *
     * Every move rewrites the object and redraws the chrome, so in a quick flick the moves
     * fall behind the pointer — the last one handled can be far short of where the button
     * went up, or there may have been none at all, which committed nothing and put the
     * object back to its original size.
     */
    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        const gesture = gestureRef.current
        if (!gesture || event.pointerId !== gesture.pointerId) return
        previewAt(event.clientX, event.clientY, event.shiftKey, event.ctrlKey)
        finishGesture(true)
    }

    useEffect(() => {
        const handleKey = (event: KeyboardEvent) => {
            const gesture = gestureRef.current
            if (!gesture) return
            // Escape abandons the resize, as in Figma; the object goes back to where it was
            if (event.key === "Escape" && event.type === "keydown") finishGesture(false)
            // pressing or releasing Shift (proportions) or Ctrl (snapping) mid-gesture takes
            // effect at once, without a move
            else if (event.key === "Shift" || event.key === "Control") {
                previewAt(gesture.lastX, gesture.lastY, event.shiftKey, event.ctrlKey)
            }
        }
        window.addEventListener("keydown", handleKey)
        window.addEventListener("keyup", handleKey)
        return () => {
            window.removeEventListener("keydown", handleKey)
            window.removeEventListener("keyup", handleKey)
        }
    })

    // a handle unmounted mid-gesture (selection changed) must not leave the document locked
    useEffect(
        () => () => {
            if (gestureRef.current) unlockDocument()
        },
        []
    )

    return (
        <Box
            // the selection chrome around this handle is pointer-events: none so that
            // clicks keep reaching the objects underneath — the handle itself opts back in
            style={{ pointerEvents: "auto" }}
            onClick={(e) => {
                e.stopPropagation()
            }}
        >
            <Box
                component="div"
                zIndex="auto"
                // keeps the hold-to-move gesture off the resize handles
                {...{ [RESIZE_HANDLE_ATTRIBUTE]: "" }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={() => finishGesture(false)}
                onLostPointerCapture={() => finishGesture(true)}
                style={{
                    // only the corners are drawn; the edges stay invisible grab zones,
                    // the way Figma does it
                    backgroundColor: isCorner ? theme.palette.common.white : "rgba(0,0,0,0)",
                    border: isCorner
                        ? `${1 / draggableScale}px solid ${theme.palette.primary.light}`
                        : undefined,
                    boxSizing: "border-box",
                    position: "absolute",
                    top: resultStylesValues?.top,
                    left: resultStylesValues?.left,
                    width: resultStylesValues?.width,
                    height: resultStylesValues?.height,
                    cursor,
                    touchAction: "none",
                    // the chrome is drawn through the object's transform; a corner keeps only
                    // its turn, so a sheared object's handles stay squares
                    transform:
                        isCorner && orientation
                            ? toCssMatrix(uprightCounterTransform(orientation.total))
                            : undefined,
                    transformOrigin: "center",
                }}
            />
        </Box>
    )
}

/**
 * memo: eight handles are mounted for the selected object and every one of them re-rendered
 * on each mousemove of a canvas drag. All props are referentially stable, so they can bail out.
 */
const DraggableItem = React.memo(DraggableItemBase)

export default DraggableItem
